import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { extractFieldErrors } from '../../../../services/practiceDetail';
import { useCreatePractice } from '../../../../hooks/usePracticeDetail';
import {
    buildPracticeCreateBody,
    emptyPracticeFieldValues,
    PRACTICE_REQUIRED_FIELDS,
    requiredFieldError
} from '../details/fieldDefinitions';
import type { DetailFieldState } from '../details/DetailField';

/**
 * Owns the practice create form's state, dirty tracking, and submit flow -
 * the create-only counterpart to usePracticeDetailsForm. There is no
 * per-field save state machine here (no "saving"/"saved" per field) because
 * a create is a single all-or-nothing POST with no existing record to patch
 * incrementally - `fieldState` only ever reports 'idle' or 'failed'.
 *
 * On success, navigates straight to the new practice's own Details tab
 * (the editor for everything created here) rather than staying on this
 * page - there is nothing left to "keep editing" once the record exists.
 */
export function usePracticeCreateForm() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { mutateAsync, isPending } = useCreatePractice();

    const [values, setValues] = useState<Record<string, string>>(() => emptyPracticeFieldValues());
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);

    // Nothing has been typed until at least one field has a non-empty value -
    // there is no "original" record to diff against, unlike the edit form.
    const isDirty = useMemo(
        () => Object.values(values).some((value) => value.trim() !== ''),
        [values]
    );

    const onFieldChange = useCallback((key: string, value: string) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        setFieldErrors((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const fieldState = useCallback(
        (key: string): DetailFieldState => (key in fieldErrors ? 'failed' : 'idle'),
        [fieldErrors]
    );
    const fieldError = useCallback((key: string) => fieldErrors[key], [fieldErrors]);

    const canCreate =
        PRACTICE_REQUIRED_FIELDS.every((field) => values[field.key]?.trim() !== '') && !isPending;

    // Guards hard navigation (refresh/close/typed URL) while anything is
    // pending - mirrors DetailsTab's beforeunload guard and useCommunicationForm's.
    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    const guardedNavigate = useCallback(
        (path: string) => {
            if (!isDirty) {
                navigate(path);
                return;
            }
            setPendingNavigationPath(path);
        },
        [isDirty, navigate]
    );

    const confirmDiscardNavigation = useCallback(() => {
        if (pendingNavigationPath) navigate(pendingNavigationPath);
        setPendingNavigationPath(null);
    }, [pendingNavigationPath, navigate]);

    const cancelDiscardNavigation = useCallback(() => {
        setPendingNavigationPath(null);
    }, []);

    const onCreate = useCallback(async () => {
        if (!canCreate) return;

        const localErrors: Record<string, string> = {};
        for (const field of PRACTICE_REQUIRED_FIELDS) {
            const message = requiredFieldError(field, values[field.key] ?? '');
            if (message) localErrors[field.key] = message;
        }
        if (Object.keys(localErrors).length > 0) {
            setFieldErrors(localErrors);
            return;
        }

        const body = buildPracticeCreateBody(values);
        const practiceName = values.name.trim();

        try {
            const created = await mutateAsync(body);
            showToast(`Practice created - ${practiceName}`, 'success');
            navigate(`/accounts/practices/${created.id}`);
        } catch (err) {
            const errors = extractFieldErrors(err);
            setFieldErrors(errors ?? {});
            showToast(
                apiErrorMessage(err, 'Failed to create practice - nothing was saved'),
                'error'
            );
        }
    }, [canCreate, values, mutateAsync, showToast, navigate]);

    return {
        values,
        fieldState,
        fieldError,
        onFieldChange,
        isDirty,
        isSaving: isPending,
        canCreate,
        onCreate: () => void onCreate(),
        guardedNavigate,
        showDiscardModal: pendingNavigationPath !== null,
        confirmDiscardNavigation,
        cancelDiscardNavigation
    };
}
